import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import Countdown from "@/components/Countdown";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";
import { ArrowLeft, Coins, Gavel, Trophy } from "lucide-react";

export default function AuctionDetail() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const { width, height } = useWindowSize();

  const load = async () => {
    try {
      const { data } = await api.get(`/auctions/${id}`);
      setData(data);
      if (data.my_bid) setAmount(String(data.my_bid.amount));
    } catch {
      toast.error("Couldn't load auction");
    }
  };
  useEffect(() => { load(); }, [id]);

  const a = data?.auction;
  const isClosed = a?.status === "closed";
  const iWon = isClosed && a?.winner_user_id === user?.user_id;

  useEffect(() => {
    if (iWon) {
      setShowConfetti(true);
      const t = setTimeout(() => setShowConfetti(false), 6000);
      return () => clearTimeout(t);
    }
  }, [iWon]);

  const submitBid = async (e) => {
    e.preventDefault();
    const val = parseInt(amount, 10);
    if (isNaN(val) || val < 0) { toast.error("Enter a non-negative number"); return; }
    if (val > (user?.points ?? 0)) { toast.error("You don't have enough points"); return; }
    setSubmitting(true);
    try {
      await api.post(`/auctions/${id}/bids`, { amount: val });
      toast.success("Bid placed! It stays sealed until close.");
      await load();
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bid failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) return <Layout><div className="brut-card p-6">Loading…</div></Layout>;

  return (
    <Layout>
      {showConfetti && <Confetti width={width} height={height} numberOfPieces={300}/>}
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm font-bold mb-4">
        <ArrowLeft strokeWidth={3} className="w-4 h-4"/> Back
      </Link>

      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-8">
          <div className="brut-card overflow-hidden bg-white">
            <div className="p-6 border-b-2 border-black bg-lilac">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {isClosed ? (
                  <span className="chip bg-mint">Closed</span>
                ) : (
                  <>
                    <span className="chip bg-sunny">Live</span>
                    <span className="chip"><Countdown target={a.closes_at} testId="auction-countdown"/></span>
                  </>
                )}
              </div>
              <h1 data-testid="auction-title" className="font-fredoka text-3xl sm:text-4xl font-bold">{a.title}</h1>
              {a.description && <p className="mt-2 text-gray-800">{a.description}</p>}
            </div>

            {isClosed ? (
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3"><Trophy strokeWidth={3} className="w-5 h-5"/><h3 className="font-fredoka text-xl font-bold">Result</h3></div>
                {a.winner_user_id ? (
                  <div className="brut-card p-5 bg-sunny">
                    <div className="font-fredoka text-2xl font-bold" data-testid="auction-winner">
                      {iWon ? "🎉 You won!" : `${data.final_bids?.[0]?.user_name || "Someone"} won`}
                    </div>
                    <div className="mt-1">Paid <b>{a.winning_price}</b> pts (2nd-price). Top bid was <b>{a.top_bid}</b>.</div>
                    <div className="mt-1 text-sm">The winner becomes the event organizer. Points are awarded if the event is marked successful.</div>
                  </div>
                ) : (
                  <div className="brut-card p-5 bg-white">No bids were placed.</div>
                )}

                <h4 className="font-fredoka text-lg font-bold mt-6 mb-2">All bids</h4>
                <ul className="space-y-2">
                  {(data.final_bids || []).map((b, i) => (
                    <li key={b.bid_id} className="flex items-center justify-between border-2 border-black rounded-lg px-3 py-2 bg-cream" data-testid={`final-bid-${i}`}>
                      <span className="font-bold">{i + 1}. {b.user_name}</span>
                      <span className="font-fredoka font-bold">{b.amount} pts</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="p-6">
                <form onSubmit={submitBid} className="space-y-4">
                  <label className="block">
                    <div className="font-fredoka font-bold mb-2 flex items-center gap-2"><Gavel strokeWidth={3} className="w-5 h-5"/>Your sealed bid</div>
                    <div className="flex items-center gap-3">
                      <input
                        data-testid="bid-amount-input"
                        type="number" min="0"
                        max={user?.points}
                        className="brut-input flex-1 text-lg"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="How many points?"
                      />
                      <div className="chip bg-sunny"><Coins strokeWidth={3} className="w-3.5 h-3.5"/>{user?.points} available</div>
                    </div>
                  </label>
                  <button data-testid="submit-bid-btn" disabled={submitting} className="brut-btn text-lg px-6 py-3">
                    {submitting ? "Placing…" : data.my_bid ? "Update my bid" : "Place sealed bid"}
                  </button>
                  {data.my_bid && (
                    <div data-testid="my-current-bid" className="text-sm text-gray-800">
                      Your current bid: <b>{data.my_bid.amount}</b> pts (hidden until close).
                    </div>
                  )}
                </form>
                <div className="mt-6 text-xs text-gray-700 border-2 border-dashed border-black rounded-lg p-3 bg-cream">
                  <b>How Vickrey works:</b> highest bidder wins, but pays the <b>2nd-highest bid</b>. Bid your true value—no gaming needed.
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="md:col-span-4">
          <div className="brut-card p-5 bg-mint">
            <div className="font-fredoka text-lg font-bold">Auction rules</div>
            <ul className="mt-2 text-sm list-disc pl-5 space-y-1">
              <li>Bids are hidden until the deadline.</li>
              <li>You can update your bid any time before close.</li>
              <li>Winner pays the 2nd-highest bid.</li>
              <li>If only one bidder, they pay their own bid.</li>
              <li>Winner is the event organizer & may earn points if the event is a success.</li>
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
}
